import { ReactNode, useMemo, useState } from "react";
import { Box, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { RateReview as ReviewIcon } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "../datagrid";
import {
  entityNameColumn,
  requesterColumn,
  reviewerCommentColumn,
  statusColumn,
  submittedColumn
} from "./columns";
import {
  OnboardingRequestBook,
  OnboardingRequestQuery,
  OnboardingRequestRowBase,
  OnboardingStatusFilter
} from "./types";

type Props<TRow extends OnboardingRequestRowBase> = {
  book: OnboardingRequestBook<TRow>;
  /** The book's admin list query. Called unconditionally, like the attachment api hooks. */
  useRequests: (filter: OnboardingStatusFilter) => OnboardingRequestQuery<TRow>;
  /**
   * The review dialog stays per book: it renders that book's request form so an admin can amend the
   * payload before approving, and those forms have nothing in common.
   */
  renderReviewDialog: (args: {
    open: boolean;
    request: TRow | null;
    onClose: () => void;
  }) => ReactNode;
};

/**
 * The admin review grid for onboarding requests, shared by all three books. Only the DTO, the query
 * and the review dialog differ.
 */
export const OnboardingRequestsAdminGrid = <
  TRow extends OnboardingRequestRowBase
>({
  book,
  useRequests,
  renderReviewDialog
}: Props<TRow>) => {
  const [filter, setFilter] = useState<OnboardingStatusFilter>("Pending");
  const [selectedRequest, setSelectedRequest] = useState<TRow | null>(null);

  const { data, isLoading, isFetching } = useRequests(filter);
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<GridColDef<TRow>[]>(
    () => [
      requesterColumn(),
      entityNameColumn(book),
      submittedColumn(),
      statusColumn(true),
      reviewerCommentColumn(),
      {
        field: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Button
            size="small"
            variant={params.row.status === "Pending" ? "contained" : "outlined"}
            startIcon={<ReviewIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRequest(params.row);
            }}
          >
            Review
          </Button>
        )
      }
    ],
    [book]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, next: OnboardingStatusFilter | null) =>
            next && setFilter(next)
          }
        >
          <ToggleButton value="Pending">Pending</ToggleButton>
          <ToggleButton value="Approved">Approved</ToggleButton>
          <ToggleButton value="Rejected">Rejected</ToggleButton>
          <ToggleButton value="All">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          loading={isLoading || isFetching}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          onRowDoubleClick={(params) => setSelectedRequest(params.row)}
        />
      </Box>

      {renderReviewDialog({
        open: selectedRequest !== null,
        request: selectedRequest,
        onClose: () => setSelectedRequest(null)
      })}
    </Box>
  );
};
